import type { OmitByValueExact, Optional } from 'utility-types'

import { assert, type EventListener, isArray, objectEntries, objectKeys, type ReadonlyNonEmptyArray, type ReadonlyRecord, throwCaptured } from '../../../utils'

import { type Capability, ConnectwareError, ConnectwareErrorType } from '../../../domain'

import type {
    AuthenticatedResource,
    BackendHeaderParameter,
    BackendHttpMethod,
    BackendJsonRequestContent,
    BackendJsonResponseContent,
    BackendPath,
    BackendPathParameters,
    BackendQueryParameters,
    BackendResponseStatuses,
} from '../../Connectware'
import { buildPath } from './Path'

/**
 * The http method being used
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'CONNECT' | 'HEAD' | 'OPTIONS' | 'PATCH' | 'TRACE'

/**
 * The possible request bodies to be sent
 */
type HttpRequestBody = ReadonlyRecord<string, unknown> | unknown[] | readonly unknown[] | string

/**
 * The response that is received by the handlers
 */
export interface HttpResponse<S extends number = number, J = unknown> {
    getStatus(): S
    getHeaders(): [string, string][]
    /**
     * @throws {ConnectwareError}
     */
    getBlob(): Promise<Blob>
    /**
     * @throws {ConnectwareError}
     */
    getText(): Promise<string>
    /**
     * @throws {ConnectwareError}
     */
    getJson(): Promise<J>
}

/**
 * The internal constructed fetch args
 */
export type HttpFetchArgs = Readonly<{
    url: ReturnType<URL['toString']>

    method: HttpMethod
    headers: ReadonlyRecord<string, string>
    body: FormData | string | null
}>

export type ConnectwareHTTPServiceOptions = Readonly<{
    baseURL: URL['origin']

    /**
     * @returns method to get token from any external state
     */
    getToken?: () => string | null

    /**
     * Request middleware
     */
    responseHandler?: Pick<EventListener<HttpResponse>, 'trigger'>
}>

type BaseArgsPart<O extends boolean, N extends string, V> = Optional<ReadonlyRecord<N, V>, O extends true ? N : never>
type RequestArgsBuldingBlock<E extends boolean, Name extends string, EValue, IValue> = E extends true
    ? OmitByValueExact<BaseArgsPart<false, Name, EValue>, never>
    : BaseArgsPart<true, Name, IValue>

type CapabilitiesSection<E extends boolean, P extends BackendPath, M extends BackendHttpMethod<P>> =
    /**
     * If external, then force the correct capability to not be present if authentication is not needed
     * Otherwise, internally it can be both
     */
    & RequestArgsBuldingBlock<E, 'capability', BackendHeaderParameter<P, M, 'Authorization'> extends never ? null : Capability | null, Capability | null>
    & /**
     * Present to help with capabilities generation script
     */ Pick<AuthenticatedResource<P>, 'capability'>

type RequestPathSection<E extends boolean, P extends BackendPath, M extends BackendHttpMethod<P>> =
    & Pick<AuthenticatedResource<P>, 'path'>
    & RequestArgsBuldingBlock<E, 'pathParams', BackendPathParameters<P, M>, ReadonlyNonEmptyArray<string>>

type RequestMethodSection<E extends boolean, Method extends HttpMethod> =
    /**
     * If external, force the coerced method, otherwise any method
     */
    Readonly<{ method: E extends true ? Method : HttpMethod }>

type RequestAuthenticationSection<E extends boolean, P extends BackendPath, M extends BackendHttpMethod<P>> =
    /**
     * How should the method authenticate
     *
     * @example true -> use from state
     * @example `string` -> use this parameter itself
     */
    RequestArgsBuldingBlock<E, 'authenticate', BackendHeaderParameter<P, M, 'Authorization'> extends never ? never : string | true, string | true>

type QueryParamsSection<E extends boolean, P extends BackendPath, M extends BackendHttpMethod<P>> =
    /**
     * Either use the coerced parameters type, or the optional general type
     */
    RequestArgsBuldingBlock<E, 'queryParams', BackendQueryParameters<P, M>, ReadonlyRecord<string, string | string[]>>

type BodySection<E extends boolean, P extends BackendPath, M extends BackendHttpMethod<P>> =
    /**
     * What body will be sent to the endpoint
     */
    RequestArgsBuldingBlock<E, 'body', BackendJsonRequestContent<P, M> | never, HttpRequestBody>

type Handler<R, O> = (response: R) => O | ConnectwareError | Promise<O | ConnectwareError>
type NarrowerHandlerMethods<P extends BackendPath, M extends BackendHttpMethod<P>, S extends BackendResponseStatuses<P, M>> =
    /**
     * Depending on the generated types, json will be excluded
     */
    Exclude<keyof HttpResponse, BackendJsonResponseContent<P, M, S> extends never ? 'getJson' : never>

type NarrowedHttpResponse<P extends BackendPath, M extends BackendHttpMethod<P>, S extends BackendResponseStatuses<P, M>> =
    /**
     * Narrow both the status, so only the given can be used, and the yielded json types
     */
    Pick<HttpResponse<S, BackendJsonResponseContent<P, M, S>>, NarrowerHandlerMethods<P, M, S>>

type NarrowedHandlers<P extends BackendPath, M extends BackendHttpMethod<P>, O> =
    /**
     * Iterate over the statuses, for each dictate the http response that can be used
     */
    Readonly<{ [S in BackendResponseStatuses<P, M>]: Handler<NarrowedHttpResponse<P, M, S>, O> }>

type HandlersSection<E extends boolean, P extends BackendPath, M extends BackendHttpMethod<P>, O> =
    /**
     * If external, limit the access to the response methods, and coerce the types
     * Otherwise make them a permissive internal structure
     */
    Readonly<{ handlers: E extends true ? NarrowedHandlers<P, M, O> : ReadonlyRecord<number, Handler<HttpResponse, O>> }>

type RequestArgs<E extends boolean, P extends BackendPath, M extends BackendHttpMethod<P>, O> =
    /**
     * Each of these sections comes together to a cohesive args type,
     * that can easily represent both the external and internal "faces" of the args
     *
     * The external being the one the implementations outside get to see
     * The internal being a more permissive alternative to allow for a simple implementation
     */
    & CapabilitiesSection<E, P, M>
    & RequestPathSection<E, P, M>
    & RequestMethodSection<E, M>
    & RequestAuthenticationSection<E, P, M>
    & QueryParamsSection<E, P, M>
    & BodySection<E, P, M>
    & HandlersSection<E, P, M, O>

export type ExternalRequestArgs<P extends BackendPath, M extends BackendHttpMethod<P>, O> = RequestArgs<true, P, M, O>
type InternalRequestArgs<P extends BackendPath, M extends BackendHttpMethod<P>, O> = RequestArgs<false, P, M, O>

export abstract class BaseConnectwareHTTPService {
    private static createRequestBodySection<P extends BackendPath, M extends BackendHttpMethod<P>, O> (
        body: InternalRequestArgs<P, M, O>['body']
    ): [HttpFetchArgs['body'], HttpFetchArgs['headers']] {
        if (body === undefined) {
            return [null, {}]
        }

        return [JSON.stringify(body), { 'Content-Type': 'application/json' }]
    }

    private static async createOutput<P extends BackendPath, M extends BackendHttpMethod<P>, O> (
        args: InternalRequestArgs<P, M, O>,
        fetchArgs: HttpFetchArgs,
        response: HttpResponse
    ): Promise<O | ConnectwareError> {
        const responseStatus = response.getStatus()
        const handler = responseStatus in args.handlers && args.handlers[responseStatus]

        if (!handler) {
            /**
             * Default error should **not** happen in production scenarios
             */
            return new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Unexpected status code in response', {
                url: fetchArgs.url,
                status: responseStatus,
                supportedStatuses: objectKeys(args.handlers),
                headers: response.getHeaders(),
            })
        }

        return await handler(response)
    }

    constructor (private readonly options: ConnectwareHTTPServiceOptions) {}

    private createAuthenticationHeaders<P extends BackendPath, M extends BackendHttpMethod<P>, O> (
        args: InternalRequestArgs<P, M, O>
    ): HttpFetchArgs['headers'] {
        let token: string | null = null

        if (typeof args.authenticate === 'string') {
            token = args.authenticate
        } else if (args.authenticate) {
            token = this.options.getToken?.() ?? null
            assert(token, new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'Token could not be found'))
        }

        return token === null ? {} : { Authorization: `Bearer ${token}` }
    }

    private createRequestUrl<P extends BackendPath, M extends BackendHttpMethod<P>, R> (args: InternalRequestArgs<P, M, R>): HttpFetchArgs['url'] {
        const url = new URL(args.pathParams ? buildPath(args.path, ...args.pathParams) : args.path, this.options.baseURL)

        if (args.queryParams) {
            /**
             * This is needed because URLSearchParams doesn't support array values directly,
             * as described in their documentation
             */
            for (const [key, valueOrValues] of objectEntries(args.queryParams)) {
                const values = isArray(valueOrValues) ? valueOrValues : [valueOrValues]
                values.forEach((value) => url.searchParams.append(key, value))
            }
        }

        return url.toString()
    }

    private createHttpFetchArgs<P extends BackendPath, M extends BackendHttpMethod<P>, O> (args: InternalRequestArgs<P, M, O>): HttpFetchArgs {
        const [body, bodyHeaders] = BaseConnectwareHTTPService.createRequestBodySection<P, M, O>(args.body)

        return {
            url: this.createRequestUrl(args),
            method: args.method,
            headers: { ...this.createAuthenticationHeaders(args), ...bodyHeaders },
            body,
        }
    }

    /**
     * Should not yield a ConnectwareError unless there is a major exception
     * A response should always be yielded
     */
    protected abstract fetch (args: HttpFetchArgs): Promise<HttpResponse>

    protected async request<P extends BackendPath, M extends BackendHttpMethod<P>, O> (args: ExternalRequestArgs<P, M, O>): Promise<O> {
        try {
            const fetchArgs = this.createHttpFetchArgs(args)
            const response = await this.fetch(fetchArgs)

            /** Notify the middleware */
            this.options.responseHandler?.trigger(response)

            const output = await BaseConnectwareHTTPService.createOutput<P, M, O>(args, fetchArgs, response)

            if (ConnectwareError.is(output)) {
                throw output
            }

            return output
        } catch (e: unknown) {
            let error: ConnectwareError

            if (ConnectwareError.is(e)) {
                error = e
            } else if (e instanceof Error) {
                /** Should **not** happen in production scenarios */
                error = new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Unexpected error', { originalMessage: e.message })
            } else {
                /** Should **not** happen in production scenarios */
                error = new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Unknown object thrown', { error: e })
            }

            // eslint-disable-next-line @typescript-eslint/unbound-method
            return throwCaptured(error, this.request)
        }
    }
}
